
import React, { createContext, useContext, useState, useCallback } from "react";
import { TradingSymbol } from "@/utils/mockData";
import fetchSymbolsFromAPI from "./fetchSymbolsFromAPI";
import { usePriceWebSocket } from "./usePriceWebSocket";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

interface TickData {
  sm: string; b: number; a: number; t: number; tu: number;
  bpc: number; apc: number; mc: number;
  b1h: number; b1c: number; b1l: number; b1o: number;
}
interface WebSocketMessage { Type: "Ticks"; PayLoad: TickData[]; }

type PriceContextType = {
  symbols: TradingSymbol[];
  loading: boolean;
  refreshData: () => void;
  isConnected: boolean;
  reconnectingStatus: boolean;
  reconnect: () => void;
};

const PriceContext = createContext<PriceContextType | undefined>(undefined);

export const PriceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [symbols, setSymbols] = useState<TradingSymbol[]>([]);
  const [loading, setLoading] = useState(false);
  const { isAuthenticated, user } = useAuth();

  const { 
    isConnected, 
    reconnectingStatus, 
    reconnect, 
    connectWebSocket 
  } = usePriceWebSocket(symbols, setSymbols, isAuthenticated);

  const loadSymbolsFromApi = useCallback(async () => {
    setLoading(true);
    try {
      const accessToken = localStorage.getItem("access_token");
      if (!accessToken) {
        setSymbols([]);
        setLoading(false);
        return;
      }
      
      let retryCount = 0;
      const maxRetries = 3;
      
      const fetchWithRetry = async (): Promise<TradingSymbol[]> => {
        try {
          const liveSymbols = await fetchSymbolsFromAPI(accessToken);
          return liveSymbols;
        } catch (error) {
          retryCount++;
          if (retryCount < maxRetries) {
            const retryDelay = 1000 * retryCount; // Increase delay with each retry
            console.info(`Retry attempt ${retryCount} for API call in ${retryDelay}ms`);
            await new Promise(resolve => setTimeout(resolve, retryDelay));
            return fetchWithRetry();
          } else {
            throw error; // Exceeded max retries
          }
        }
      };
      
      const liveSymbols = await fetchWithRetry();
      
      // Mark favorite symbols based on user profile
      const symbolsWithFavorites = liveSymbols.map(symbol => ({
        ...symbol,
        isFavorite: user?.favorite_symbols?.includes(symbol.name) || false
      }));
      
      setSymbols(symbolsWithFavorites);
    } catch (error) {
      setSymbols([]);
      toast.error("Failed to fetch live trading symbols.", {
        description: "Please try again in a moment.",
        action: {
          label: "Retry",
          onClick: () => loadSymbolsFromApi()
        }
      });
    } finally {
      setLoading(false);
    }
  }, [user?.favorite_symbols]);

  // Refresh data and reload from API, reconnect websocket as needed
  const refreshData = useCallback(() => {
    setLoading(true);
    loadSymbolsFromApi()
      .then(() => {
        if (!isConnected) {
          connectWebSocket();
        }
      })
      .finally(() => setLoading(false));
  }, [loadSymbolsFromApi, connectWebSocket, isConnected]);

  // On login status change, fetch symbols if authenticated, else reset state
  React.useEffect(() => {
    if (isAuthenticated) {
      loadSymbolsFromApi();
    } else {
      setSymbols([]);
    }
  }, [isAuthenticated, loadSymbolsFromApi]);

  return (
    <PriceContext.Provider value={{ 
      symbols, 
      loading, 
      refreshData, 
      isConnected, 
      reconnectingStatus, 
      reconnect 
    }}>
      {children}
    </PriceContext.Provider>
  );
};

export const usePriceContext = () => {
  const context = useContext(PriceContext);
  if (context === undefined) {
    throw new Error("usePriceContext must be used within a PriceProvider");
  }
  return context;
};
